import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
	updatePendingShareStatusHousesHouseIdDocumentsPendingShareIdStatusPutMutation,
	listReceivedPendingSharesHousesHouseIdDocumentsPendingReceivedGetQueryKey,
	getNotificationsHousesHouseIdNotificationsGetQueryKey,
	listDocumentsHousesHouseIdDocumentsGetQueryKey,
} from '@/client/@tanstack/react-query.gen';

/**
 * Hook to update the status of a pending document share (accept or reject)
 *
 * Purpose: Allows property owners to accept or reject document shares from builders/professionals.
 * On success, invalidates related queries to ensure UI reflects the updated state.
 * When accepting, also invalidates the documents list since accepted shares become documents.
 */
/**
 * Shared key for accept/reject mutations so the record list can detect an in-flight review by
 * share id (via useMutationState) even after the drawer that fired it has closed/unmounted.
 */
export const PENDING_SHARE_STATUS_MUTATION_KEY = ['pendingShareStatus'] as const;

export const useUpdatePendingShareStatus = (houseId: number) => {
	const queryClient = useQueryClient();

	const baseMutation = updatePendingShareStatusHousesHouseIdDocumentsPendingShareIdStatusPutMutation();

	const mutation = useMutation({
		...baseMutation,
		mutationKey: PENDING_SHARE_STATUS_MUTATION_KEY,
		// Do the PUT *and* the list refreshes inside the mutationFn so the mutation stays `pending`
		// through both. The mutation's status tracks the mutationFn only — if the refetch ran after
		// mutateAsync resolved, status would flip to success on the PUT and the per-row Review spinner
		// would stop mid-refetch, flashing "Review" before the row transitions. Keeping it here holds the
		// spinner until the record has materialised / dropped out, so it hands straight off to the row.
		mutationFn: async (...args: Parameters<NonNullable<typeof baseMutation.mutationFn>>) => {
			const result = await baseMutation.mutationFn!(...args);
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: listReceivedPendingSharesHousesHouseIdDocumentsPendingReceivedGetQueryKey({
						path: { house_id: houseId },
					}),
				}),
				// Invalidate notifications query to update notification list
				queryClient.invalidateQueries({
					queryKey: getNotificationsHousesHouseIdNotificationsGetQueryKey({
						path: { house_id: houseId },
					}),
				}),
				// Refetch documents list - accepted shares become documents and must be immediately available
				queryClient.refetchQueries({
					queryKey: listDocumentsHousesHouseIdDocumentsGetQueryKey({
						path: { house_id: houseId },
					}),
				}),
			]);
			return result;
		},
	});

	const updateStatus = async (
		shareId: number,
		status: 'accepted' | 'rejected',
		resolutionNote?: string,
		isPrivate?: boolean,
	) => {
		return mutation.mutateAsync({
			body: {
				status,
				resolution_note: resolutionNote ?? null,
				// Only sent on accept: the owner chooses the pushed copy's visibility as it's cloned in.
				...(isPrivate !== undefined ? { private: isPrivate } : {}),
			},
			path: {
				house_id: houseId,
				share_id: shareId,
			},
		});
	};

	return { updateStatus, ...mutation };
};

export default useUpdatePendingShareStatus;
